import i18n from 'i18next'
import store from '../../redux/store'
import LayoutActions from '../../redux/actions/Layout'
import { BROWSER_NOTIFICATION_PERMISSION_VALUES } from '../../constants/general'
import {
  resolveMessageNotificationCategoryId,
  findMessageCategoryInStore
} from '../../constants/messageCategoryIds'
import { flashTitleForIncomingMessage } from './messageTitleFlash'
import {
  getDevicesByIdFromState,
  resolveOpenChatDeviceId,
  resolveOpenChatThreadFromMessage
} from '../chat/openChatThread'

/** Bundled sound for the settings-page “example notification” demo only — not used for live messages. */
const EXAMPLE_NOTIFICATION_SOUND_PATH = '/sounds/error-message.mp3'
const NOTIFICATION_ICON = '/android-chrome-192x192.png'
/** Shared tag so rapid incoming messages collapse into a single OS notification instead of stacking. */
const NOTIFICATION_TAG = 'wss-new-message'
const EXAMPLE_NOTIFICATION_TAG = 'wss-notification-example'
const SERVICE_WORKER_PATH = '/sw.js'

/** Cache one Audio element per source so we don't re-download the sound on every message. */
const audioBySource = {}

const getAudio = src => {
  if (!src || typeof window === 'undefined' || typeof window.Audio === 'undefined') return null
  if (!audioBySource[src]) {
    // Do not set crossOrigin — CDN audio is playback-only; anonymous mode requires CORS headers the bucket may not expose.
    audioBySource[src] = new window.Audio(src)
  }
  return audioBySource[src]
}

/** Drop a cached Audio element so the next play loads fresh media (e.g. after sound upload). */
export const invalidateMessageSoundCache = src => {
  if (!src) return
  delete audioBySource[src]
}

/**
 * Prime autoplay during a user gesture (file pick / save click) so later socket messages can play sound.
 * @param {string} src
 */
export const primeNotificationAudioSource = src => {
  if (!src) return
  const audio = getAudio(src)
  if (!audio) return
  try {
    audio.currentTime = 0
    const playback = audio.play()
    if (playback && typeof playback.then === 'function') {
      playback
        .then(() => {
          audio.pause()
          audio.currentTime = 0
        })
        .catch(() => {})
    }
  } catch { /* ignore */ }
}

/** Prime every configured category sound during a user gesture (profile demo / category save). */
export const primeConfiguredMessageCategorySounds = (messageCategoriesById = {}) => {
  Object.values(messageCategoriesById).forEach(category => {
    const soundPath = category?.soundPath
    if (soundPath != null && String(soundPath).trim() !== '') {
      primeNotificationAudioSource(String(soundPath).trim())
    }
  })
}

/**
 * Resolve the sound source for a message from its category configuration.
 * Returns null when the category has no sound file — callers must not play audio.
 * @param {object} message - normalized store message ({ categoryId, ... })
 * @param {Object<string, object>} messageCategoriesById - data.messageCategories store map
 * @returns {string|null}
 */
export const resolveMessageSoundSource = (message, messageCategoriesById = {}) => {
  const categoryId = resolveMessageNotificationCategoryId(message)
  const categoryName = message?.categoryName ?? message?.category?.name
  const category = findMessageCategoryInStore(messageCategoriesById, categoryId, categoryName)
  const soundPath = category?.soundPath
  if (soundPath == null || String(soundPath).trim() === '') {
    return null
  }
  return String(soundPath).trim()
}

/**
 * Play the per-category notification sound when configured. Autoplay rejections — which
 * happen until the user has interacted with the page — are swallowed.
 */
export const playMessageSound = (message, messageCategoriesById = {}) => {
  const src = resolveMessageSoundSource(message, messageCategoriesById)
  if (!src) return
  const audio = getAudio(src)
  if (!audio) return
  try {
    audio.currentTime = 0
    const playback = audio.play()
    if (playback && typeof playback.catch === 'function') {
      playback.catch(() => { /* autoplay blocked until a user gesture; ignore */ })
    }
  } catch { /* Audio unsupported / blocked; ignore */ }
}

/**
 * True when browser notifications are enabled in the profile and the OS permission is granted.
 */
export const canShowBrowserNotification = browserNotificationEnabled =>
  typeof window !== 'undefined' &&
  'Notification' in window &&
  !!browserNotificationEnabled &&
  window.Notification.permission === BROWSER_NOTIFICATION_PERMISSION_VALUES.GRANTED

/**
 * True when the operator isn't actively looking at the app (tab hidden or window unfocused),
 * mirroring the legacy "only notify when there is no focused client" behavior.
 */
export const isAppInBackground = () => {
  if (typeof document === 'undefined') return true
  if (document.hidden) return true
  if (typeof document.hasFocus === 'function') return !document.hasFocus()
  return false
}

const openIncomingChat = ({ message, senderDeviceId, receiverChatGroupId }) => {
  if (receiverChatGroupId) {
    store.dispatch(LayoutActions.openMainChatForChatGroup(String(receiverChatGroupId)))
    return
  }
  const devicesById = getDevicesByIdFromState(store.getState())
  const thread = message
    ? resolveOpenChatThreadFromMessage(message, devicesById)
    : { deviceId: resolveOpenChatDeviceId(senderDeviceId, devicesById), userId: undefined }
  if (thread.deviceId) {
    store.dispatch(LayoutActions.openMainChatForDevice(thread.deviceId))
  }
}

/**
 * Show a foreground browser notification for an incoming message.
 * @param {object} params
 * @param {string} params.title
 * @param {string} [params.body]
 * @param {function} [params.onClick]
 */
export const showMessageNotification = ({ title, body, onClick, tag = NOTIFICATION_TAG }) => {
  if (typeof window === 'undefined' || !('Notification' in window)) return null
  try {
    const notification = new window.Notification(title, {
      body: body || '',
      icon: NOTIFICATION_ICON,
      tag
    })
    notification.onclick = () => {
      try { window.focus() } catch { /* focus may be blocked; ignore */ }
      onClick?.()
      notification.close()
    }
    return notification
  } catch {
    return null
  }
}

const getServiceWorkerRegistration = async () => {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null
  let registration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_PATH)
  if (!registration) {
    registration = await navigator.serviceWorker.register(SERVICE_WORKER_PATH)
  }
  await navigator.serviceWorker.ready
  return registration
}

/**
 * Request permission (if needed) and show a demo notification from the profile page.
 * Uses the service worker when available — more reliable on macOS Chrome while the tab is focused.
 * @returns {Promise<{ ok: true } | { ok: false, reason: 'unsupported'|'denied'|'dismissed'|'failed' }>}
 */
export const showExampleBrowserNotification = async () => {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return { ok: false, reason: 'unsupported' }
  }

  let permission = window.Notification.permission
  if (permission !== BROWSER_NOTIFICATION_PERMISSION_VALUES.GRANTED) {
    permission = await window.Notification.requestPermission()
  }
  if (permission === BROWSER_NOTIFICATION_PERMISSION_VALUES.DENIED) {
    return { ok: false, reason: 'denied' }
  }
  if (permission !== BROWSER_NOTIFICATION_PERMISSION_VALUES.GRANTED) {
    return { ok: false, reason: 'dismissed' }
  }

  const title = i18n.t('Notifications.Example.Title', 'Example notification')
  const body = i18n.t('Notifications.Example.Body', 'Browser notifications are working.')
  const options = {
    body,
    icon: NOTIFICATION_ICON,
    tag: EXAMPLE_NOTIFICATION_TAG
  }

  try {
    const registration = await getServiceWorkerRegistration()
    if (registration) {
      await registration.showNotification(title, options)
    } else {
      const notification = showMessageNotification({ title, body, tag: EXAMPLE_NOTIFICATION_TAG })
      if (!notification) return { ok: false, reason: 'failed' }
    }

    const demoAudio = getAudio(EXAMPLE_NOTIFICATION_SOUND_PATH)
    if (demoAudio) {
      try {
        demoAudio.currentTime = 0
        const playback = demoAudio.play()
        if (playback && typeof playback.catch === 'function') {
          playback.catch(() => {})
        }
      } catch { /* ignore */ }
    }
    return { ok: true }
  } catch {
    const notification = showMessageNotification({ title, body, tag: EXAMPLE_NOTIFICATION_TAG })
    if (!notification) return { ok: false, reason: 'failed' }
    const demoAudio = getAudio(EXAMPLE_NOTIFICATION_SOUND_PATH)
    if (demoAudio) {
      try {
        demoAudio.currentTime = 0
        demoAudio.play()?.catch?.(() => {})
      } catch { /* ignore */ }
    }
    return { ok: true }
  }
}

/**
 * Orchestrates the incoming-message UX: always plays the (per-category) sound, flashes the window
 * title, and optionally raises a browser notification when enabled, permitted, and backgrounded.
 * @param {object} params
 * @param {object} params.message - normalized store message
 * @param {Object<string, object>} params.messageCategoriesById - data.messageCategories store map
 * @param {boolean} params.browserNotificationEnabled - profile.browserNotification
 * @param {string} [params.senderName] - device/sender name used as the notification title
 * @param {string} [params.senderDeviceId] - opens the device chat thread on notification click
 * @param {string} [params.receiverChatGroupId] - opens the group chat thread on notification click
 * @returns {{ notified: boolean }}
 */
export const notifyIncomingMessage = ({
  message,
  messageCategoriesById,
  browserNotificationEnabled,
  senderName,
  senderDeviceId,
  receiverChatGroupId
}) => {
  playMessageSound(message, messageCategoriesById)
  flashTitleForIncomingMessage({ senderName: senderName || i18n.t('Notifications.NewMessage.Title', 'New message') })

  const chatTarget = {
    message,
    senderDeviceId,
    receiverChatGroupId: receiverChatGroupId ?? message?.receiverChatGroupId
  }

  if (canShowBrowserNotification(browserNotificationEnabled) && isAppInBackground()) {
    showMessageNotification({
      title: senderName || i18n.t('Notifications.NewMessage.Title', 'New message'),
      body: message?.content,
      onClick: () => openIncomingChat(chatTarget)
    })
    return { notified: true }
  }

  return { notified: false }
}

/** @internal test helper */
export const __resetAudioCacheForTests = () => {
  Object.keys(audioBySource).forEach(key => delete audioBySource[key])
}
